import type { HTMLAttributes, ReactNode } from 'react'
import { cn } from './cn'
import { t } from '@/lib/i18n'

export type BadgeTone = 'accent' | 'navy' | 'gray' | 'success' | 'danger' | 'warning'

// accent is the highlight chip ("7 day free trial", plan, price, "Pinned"): gold family so the only colour beside a
// price is the gold CTA (REDESIGN 9, Q-LOOK-04). MG cyan stays in the logo, avatar, progress fill and focus ring.
const tones: Record<BadgeTone, string> = {
  accent: 'bg-cta-tint text-on-cta-tint',
  navy: 'bg-navy-tint text-on-navy-tint',
  gray: 'bg-surface-2 text-muted',
  success: 'bg-success-tint text-success',
  danger: 'bg-danger-tint text-danger',
  warning: 'bg-warning-tint text-warning',
}

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone
  icon?: ReactNode
}

/** Small tone pill (h20, px8, 12/600), for example "Pinned", "Free", "Live". */
export function Badge({ tone = 'gray', icon, className, children, ...rest }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex h-5 shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 text-tiny [&_svg]:size-3',
        tones[tone],
        className,
      )}
      {...rest}
    >
      {icon}
      {children}
    </span>
  )
}

export interface CountBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  count: number
  /** Counts above max render as "max+". Default 9 (top bar style "9+"). */
  max?: number
  /** Hide when count is 0 (default true). */
  hideZero?: boolean
}

/** Red unread count pill (min 18x18, white 11/700). */
export function CountBadge({ count, max = 9, hideZero = true, className, ...rest }: CountBadgeProps) {
  if (hideZero && count <= 0) return null
  const label = count > max ? `${max}+` : String(count)
  return (
    <span
      className={cn(
        'inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-count px-[5px]',
        'text-[11px] leading-none font-bold text-white tabular-nums',
        className,
      )}
      {...rest}
    >
      {label}
    </span>
  )
}

export type Role = 'owner' | 'admin' | 'moderator' | 'member'

// Literal t() calls (run at render), so the role names reach the translators.
const roleLabels: Record<Role, () => string> = {
  owner: () => t('Owner'),
  admin: () => t('Admin'),
  moderator: () => t('Moderator'),
  member: () => t('Member'),
}

export interface RolePillProps {
  role: Role | string
  className?: string
}

/** Owner, Admin and Moderator pills (navy tone). Members render nothing. */
export function RolePill({ role, className }: RolePillProps) {
  const key = String(role).toLowerCase() as Role
  if (key === 'member' || !Object.hasOwn(roleLabels, key)) return null
  return (
    <Badge tone="navy" className={className}>
      {roleLabels[key]()}
    </Badge>
  )
}
